/**
 * MaterialBankService — Banque hebdomadaire de recettes MATIÈRES par cat/sous-cat
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La banque est générée 1×/semaine en backend (Lambda outfit-material-bank-cron).
 * Chaque entrée = 1 recette matière pour une (catégorie, sous-catégorie) :
 *   { primary_material, alternatives, works_with, avoid_with,
 *     formality_min, formality_max, best_seasons, best_occasions,
 *     versatility, description }
 *
 * Consommée par scoreMaterialHarmony (OutfitEngine) et scoreItemAgainstPiece
 * (StyleBankService) pour pondérer le matching matières par cat/sous-cat.
 *
 * Usage :
 *   import { getMaterialBank, getRecipesFor, scoreItemMaterial } from './MaterialBankService';
 *   const bank = await getMaterialBank();
 *   const score = scoreItemMaterial(item, bank); // 0-1
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AWS_CONFIG } from '../config/aws_config';
import { getAuthToken } from './authHelpers';

const API_URL = AWS_CONFIG.api?.baseUrl || AWS_CONFIG.apiUrl || '';
const CACHE_KEY = 'material_bank_v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export async function getMaterialBank({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const age = Date.now() - (parsed._cachedAt || 0);
        if (age < CACHE_TTL_MS && Array.isArray(parsed.recipes)) {
          console.log(`[MaterialBank] 💾 cache hit (${parsed.recipes.length} recipes, age ${Math.round(age / 1000 / 3600)}h)`);
          return { ...parsed, byKey: buildByKey(parsed.recipes), fromCache: true };
        }
      }
    } catch (e) {
      console.warn('[MaterialBank] cache read error:', e.message);
    }
  }

  try {
    const url = `${API_URL}/material-bank`;
    const token = await getAuthToken().catch(() => null);
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    console.log('[MaterialBank] 🌐 fetching', url, token ? '(with auth)' : '(no auth)');
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`API ${res.status}`);
    const data = await res.json();

    const toCache = { recipes: data.recipes || [], week_of: data.week_of, total: data.total, _cachedAt: Date.now() };
    try { await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(toCache)); } catch {}

    console.log(`[MaterialBank] ✓ fetched ${data.recipes?.length || 0} recipes (week=${data.week_of})`);
    return { ...toCache, byKey: buildByKey(toCache.recipes), fromCache: false };
  } catch (e) {
    console.warn('[MaterialBank] fetch error:', e.message);
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.recipes)) {
          console.log('[MaterialBank] ↩️ stale cache fallback');
          return { ...parsed, byKey: buildByKey(parsed.recipes), fromCache: true, stale: true };
        }
      }
    } catch {}
    return { recipes: [], byKey: {}, week_of: null, fromCache: false, error: e.message };
  }
}

function buildByKey(recipes) {
  const map = {};
  for (const r of recipes) {
    const key = `${r.category}:${(r.subcategory || '').toLowerCase()}`;
    if (!map[key]) map[key] = [];
    map[key].push(r);
  }
  return map;
}

/**
 * Récupère les recettes pour une (category, subcategory) avec fallback fuzzy.
 */
export function getRecipesFor(bank, category, subcategory) {
  if (!bank?.byKey || !category) return [];
  const subLower = (subcategory || '').toLowerCase().trim();
  const exactKey = `${category}:${subLower}`;
  if (bank.byKey[exactKey]?.length > 0) return bank.byKey[exactKey];
  const candidates = [];
  for (const key of Object.keys(bank.byKey)) {
    if (!key.startsWith(`${category}:`)) continue;
    const keySub = key.split(':')[1] || '';
    if (subLower && (keySub.includes(subLower) || subLower.includes(keySub))) {
      candidates.push(...bank.byKey[key]);
    }
  }
  return candidates;
}

/**
 * Score de qualité matière d'un item par rapport au material bank (0-1).
 * Compare item.ai_metadata.material aux recettes recommandées pour sa
 * (category, subcategory). Retourne :
 *   - 1.0 si match exact (item.material === primary_material)
 *   - 0.85 si item.material dans alternatives
 *   - 0.7 si item.material dans works_with d'une recette
 *   - 0.4 si item.material dans avoid_with (pénalité)
 *   - 0.5 si pas de match mais data présent
 *   - 0.6 si pas de data (neutre)
 *
 * Match tolérant : substring + lowercase (ex: "coton bio" matche "coton").
 *
 * @param {Object} item Item du dressing/catalogue (avec ai_metadata)
 * @param {Object} bank Material bank
 * @returns {{ score: number, matchedRecipe: Object|null, reason: string }}
 */
export function scoreItemMaterial(item, bank) {
  if (!bank?.byKey || !item?.ai_metadata) return { score: 0.6, matchedRecipe: null, reason: 'no-data' };
  const ai = item.ai_metadata;
  const subcategory = ai.subcategory || ai.detectedType;
  const category = item.category;
  if (!category || !subcategory) return { score: 0.6, matchedRecipe: null, reason: 'no-subcat' };

  const recipes = getRecipesFor(bank, category, subcategory);
  if (recipes.length === 0) return { score: 0.6, matchedRecipe: null, reason: 'no-recipes' };

  const itemMaterial = (ai.material || '').toLowerCase().trim();
  if (!itemMaterial) return { score: 0.6, matchedRecipe: null, reason: 'no-material-on-item' };

  const matches = (target) => {
    const t = (target || '').toLowerCase().trim();
    if (!t) return false;
    return t === itemMaterial
        || t.includes(itemMaterial)
        || itemMaterial.includes(t);
  };

  let best = { score: 0.5, recipe: null, reason: 'no-match' };

  for (const r of recipes) {
    // 1. Exact match avec primary_material → 1.0
    if (matches(r.primary_material)) {
      return { score: 1.0, matchedRecipe: r, reason: 'exact-primary' };
    }

    // 2. Match avec alternatives → 0.85
    const alternatives = r.alternatives || [];
    if (alternatives.some(a => matches(a))) {
      if (best.score < 0.85) best = { score: 0.85, recipe: r, reason: 'alternative' };
    }

    // 3. Présent en works_with d'une recette → 0.7 (matière acceptable en combinaison)
    const worksWith = r.works_with || [];
    if (worksWith.some(w => matches(w))) {
      if (best.score < 0.7) best = { score: 0.7, recipe: r, reason: 'works-with' };
    }

    // 4. Présent en avoid_with → 0.4 (pénalité)
    const avoidWith = r.avoid_with || [];
    if (avoidWith.some(a => matches(a))) {
      if (best.score > 0.4) best = { score: 0.4, recipe: r, reason: 'avoid-listed' };
    }
  }

  return { score: best.score, matchedRecipe: best.recipe, reason: best.reason };
}
